import { useReducer, useCallback, useMemo } from 'react'

export const ACTION_TYPES = {
  SET_DAG: 'SET_DAG',
  UPDATE_DAG_META: 'UPDATE_DAG_META',
  ADD_STEP: 'ADD_STEP',
  UPDATE_STEP: 'UPDATE_STEP',
  REMOVE_STEP: 'REMOVE_STEP',
  SET_PARENT: 'SET_PARENT',
  REORDER_STEPS: 'REORDER_STEPS',
  SELECT_STEP: 'SELECT_STEP',
  DESELECT_STEP: 'DESELECT_STEP',
  MARK_CLEAN: 'MARK_CLEAN'
}

const DEFAULT_STEP = {
  dagStepId: '',
  name: '',
  targetAction: 'pass',
  targetIdentifier: null,
  triggerEventType: null,
  isAsync: false,
  completeOn: null,
  failOn: null,
  parentStepId: null,
  configuration: {}
}

function createInitialState (dag, isNew) {
  if (isNew) {
    return {
      dag: { dagId: '', name: '', configuration: {} },
      steps: [{ ...DEFAULT_STEP, dagStepId: 'start', name: 'Start', targetAction: 'start' }],
      dirty: false,
      selectedStepId: null,
      isNew: true
    }
  }
  return {
    dag: { dagId: dag.dagId, name: dag.name, configuration: dag.configuration ?? {}, delegatedIdentityCode: dag.delegatedIdentityCode ?? null },
    steps: (dag.steps ?? []).map(s => ({ ...s })),
    dirty: false,
    selectedStepId: null,
    isNew: false
  }
}

function dagEditorReducer (state, action) {
  switch (action.type) {
    case ACTION_TYPES.SET_DAG: {
      const { dag, isNew } = action.payload
      return createInitialState(dag, isNew)
    }

    case ACTION_TYPES.UPDATE_DAG_META: {
      const updates = action.payload
      return {
        ...state,
        dag: { ...state.dag, ...updates },
        dirty: true
      }
    }

    case ACTION_TYPES.ADD_STEP: {
      const newStep = { ...DEFAULT_STEP, ...action.payload }
      return {
        ...state,
        steps: [...state.steps, newStep],
        dirty: true,
        selectedStepId: newStep.dagStepId
      }
    }

    case ACTION_TYPES.UPDATE_STEP: {
      const { dagStepId, ...updates } = action.payload
      return {
        ...state,
        steps: state.steps.map(s =>
          s.dagStepId === dagStepId ? { ...s, ...updates } : s
        ),
        dirty: true
      }
    }

    case ACTION_TYPES.REMOVE_STEP: {
      const removeId = action.payload
      if (removeId === 'start') return state
      const removedStep = state.steps.find(s => s.dagStepId === removeId)
      if (!removedStep) return state
      const reparentTo = removedStep.parentStepId ?? null
      return {
        ...state,
        steps: state.steps
          .filter(s => s.dagStepId !== removeId)
          .map(s => s.parentStepId === removeId ? { ...s, parentStepId: reparentTo } : s),
        dirty: true,
        selectedStepId: state.selectedStepId === removeId ? null : state.selectedStepId
      }
    }

    case ACTION_TYPES.SET_PARENT: {
      const { dagStepId, parentStepId } = action.payload
      return {
        ...state,
        steps: state.steps.map(s =>
          s.dagStepId === dagStepId ? { ...s, parentStepId } : s
        ),
        dirty: true
      }
    }

    case ACTION_TYPES.REORDER_STEPS: {
      const { orderedIds } = action.payload
      const stepMap = {}
      state.steps.forEach(s => { stepMap[s.dagStepId] = s })
      const reordered = orderedIds.map((id, i) => ({
        ...stepMap[id],
        parentStepId: i === 0 ? null : orderedIds[i - 1]
      }))
      const remaining = state.steps.filter(s => !orderedIds.includes(s.dagStepId))
      return {
        ...state,
        steps: [...reordered, ...remaining],
        dirty: true
      }
    }

    case ACTION_TYPES.SELECT_STEP:
      return { ...state, selectedStepId: action.payload }

    case ACTION_TYPES.DESELECT_STEP:
      return { ...state, selectedStepId: null }

    case ACTION_TYPES.MARK_CLEAN:
      return { ...state, dirty: false }

    default:
      return state
  }
}

export function useDagEditor (initialDag, isNew = false) {
  const [state, dispatch] = useReducer(
    dagEditorReducer,
    { dag: initialDag, isNew },
    ({ dag, isNew: n }) => createInitialState(dag ?? {}, n)
  )

  const selectedStep = useMemo(() => {
    if (!state.selectedStepId) return null
    return state.steps.find(s => s.dagStepId === state.selectedStepId) ?? null
  }, [state.selectedStepId, state.steps])

  const setDag = useCallback((dag, isNewFlag) => {
    dispatch({ type: ACTION_TYPES.SET_DAG, payload: { dag, isNew: isNewFlag } })
  }, [])

  const updateDagMeta = useCallback((updates) => {
    dispatch({ type: ACTION_TYPES.UPDATE_DAG_META, payload: updates })
  }, [])

  const addStep = useCallback((step) => {
    dispatch({ type: ACTION_TYPES.ADD_STEP, payload: step })
  }, [])

  const updateStep = useCallback((updates) => {
    dispatch({ type: ACTION_TYPES.UPDATE_STEP, payload: updates })
  }, [])

  const removeStep = useCallback((dagStepId) => {
    dispatch({ type: ACTION_TYPES.REMOVE_STEP, payload: dagStepId })
  }, [])

  const setParent = useCallback((dagStepId, parentStepId) => {
    dispatch({ type: ACTION_TYPES.SET_PARENT, payload: { dagStepId, parentStepId } })
  }, [])

  const reorderSteps = useCallback((orderedIds) => {
    dispatch({ type: ACTION_TYPES.REORDER_STEPS, payload: { orderedIds } })
  }, [])

  const selectStep = useCallback((dagStepId) => {
    dispatch({ type: ACTION_TYPES.SELECT_STEP, payload: dagStepId })
  }, [])

  const deselectStep = useCallback(() => {
    dispatch({ type: ACTION_TYPES.DESELECT_STEP })
  }, [])

  const markClean = useCallback(() => {
    dispatch({ type: ACTION_TYPES.MARK_CLEAN })
  }, [])

  return {
    state,
    selectedStep,
    actions: {
      setDag,
      updateDagMeta,
      addStep,
      updateStep,
      removeStep,
      setParent,
      reorderSteps,
      selectStep,
      deselectStep,
      markClean
    }
  }
}
